/**
 * SMS Service for sending invite links
 * 
 * NOTE: This requires a backend service (e.g., Twilio, AWS SNS, etc.)
 * For now, this is a placeholder that will call your backend API
 * 
 * Setup required:
 * 1. Sign up for Twilio (https://www.twilio.com) or similar service
 * 2. Create a backend endpoint (e.g., /api/send-sms)
 * 3. Store your API keys securely (never in frontend code)
 * 4. Update the API endpoint URL below
 */

// Update this to your backend endpoint
// For local development: http://localhost:3001/api/send-sms
// For production (Firebase Functions): https://us-central1-hotelia-d966f.cloudfunctions.net/sendSMS
const SMS_API_ENDPOINT = process.env.REACT_APP_SMS_API_URL || 'https://us-central1-hotelia-d966f.cloudfunctions.net/sendSMS';

export interface SendSMSOptions {
  to: string; // Phone number in E.164 format (e.g., +1234567890)
  message: string;
}

/**
 * Send an SMS message via backend API
 * This is a placeholder - you'll need to implement the backend endpoint
 */
export const sendSMS = async (options: SendSMSOptions): Promise<void> => {
  try {
    const response = await fetch(SMS_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: options.to,
        message: options.message,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send SMS');
    }

    return;
  } catch (error) {
    console.error('Error sending SMS:', error);
    throw error;
  }
};

/**
 * Send an invite link via SMS (for general signup invites)
 * 
 * @param phoneNumber - Recipient's phone number
 * @param inviteURL - The invite link
 * @param inviterName - Name of person sending invite
 * @param recipientUserId - User ID of recipient (optional, for consent checking)
 * @param senderUserId - User ID of sender (optional, for friends_only check)
 */
export const sendInviteSMS = async (
  phoneNumber: string,
  inviteURL: string,
  inviterName?: string,
  recipientUserId?: string,
  senderUserId?: string
): Promise<void> => {
  // Check SMS consent if recipient user ID is provided (they're already a user)
  if (recipientUserId) {
    const { canSendSMS } = await import('./sms-consent');
    const canSend = await canSendSMS(recipientUserId, senderUserId);
    
    if (!canSend) {
      console.log(`[SMS] Skipping SMS to ${recipientUserId} - user has opted out or doesn't allow from this sender`);
      throw new Error('User has opted out of SMS or only accepts from friends');
    }
  }
  // If no recipientUserId, they're not a user yet, so we can send (they'll consent on signup)

  const message = inviterName
    ? `${inviterName} invited you to join Hotelia! Sign up here: ${inviteURL}`
    : `You've been invited to join Hotelia! Sign up here: ${inviteURL}`;

  return sendSMS({
    to: phoneNumber,
    message,
  });
};

/**
 * Send an event invite via SMS
 * 
 * @param phoneNumber - Recipient's phone number
 * @param inviteURL - The invite link
 * @param eventTitle - Event title
 * @param inviterName - Name of person sending invite
 * @param recipientUserId - User ID of recipient (for consent checking)
 * @param senderUserId - User ID of sender (for friends_only check)
 */
export const sendEventInviteSMS = async (
  phoneNumber: string,
  inviteURL: string,
  eventTitle: string,
  inviterName?: string,
  recipientUserId?: string,
  senderUserId?: string
): Promise<void> => {
  // Check SMS consent if recipient user ID is provided
  if (recipientUserId) {
    const { canSendSMS } = await import('./sms-consent');
    const canSend = await canSendSMS(recipientUserId, senderUserId);
    
    if (!canSend) {
      console.log(`[SMS] Skipping SMS to ${recipientUserId} - user has opted out or doesn't allow from this sender`);
      throw new Error('User has opted out of SMS or only accepts from friends');
    }
  }

  const message = inviterName
    ? `${inviterName} invited you to "${eventTitle}" on Hotelia! Accept here: ${inviteURL}`
    : `You've been invited to "${eventTitle}" on Hotelia! Accept here: ${inviteURL}`;

  return sendSMS({
    to: phoneNumber,
    message,
  });
};

/**
 * Send a group invite via SMS
 * 
 * @param phoneNumber - Recipient's phone number
 * @param inviteURL - The invite link
 * @param groupName - Group name
 * @param inviterName - Name of person sending invite
 * @param recipientUserId - User ID of recipient (for consent checking)
 * @param senderUserId - User ID of sender (for friends_only check)
 */
export const sendGroupInviteSMS = async (
  phoneNumber: string,
  inviteURL: string,
  groupName: string,
  inviterName?: string,
  recipientUserId?: string,
  senderUserId?: string
): Promise<void> => {
  // Check SMS consent if recipient user ID is provided
  if (recipientUserId) {
    const { canSendSMS } = await import('./sms-consent');
    const canSend = await canSendSMS(recipientUserId, senderUserId);
    
    if (!canSend) {
      console.log(`[SMS] Skipping SMS to ${recipientUserId} - user has opted out or doesn't allow from this sender`);
      throw new Error('User has opted out of SMS or only accepts from friends');
    }
  }  const message = inviterName
    ? `${inviterName} invited you to join "${groupName}" on Hotelia! Join here: ${inviteURL}`
    : `You've been invited to join "${groupName}" on Hotelia! Join here: ${inviteURL}`;

  return sendSMS({
    to: phoneNumber,
    message,
  });
};